import { APPLE_PAY_VERSION, MERCHANT_NAME } from '@/constants/applePay';
import useAnalytics from '@/hooks/use-analytics';
import { reportError } from '@/services/error-reporting-service';
import { styled } from '@mui/material';
import { ApplePay, applePay, ApplePayPayload, ApplePayPaymentRequest, Client, ApplePaySession } from 'braintree-web';
import { useEffect, useRef, useState } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
interface BraintreeApplePayProps {
  onSuccess: (nonce: string) => Promise<void>;
  clientInstance: Client;
  legal: string;
  amount: string;
  fieldProps: ControllerRenderProps;
  error: FieldError | undefined;
}

declare global {
  interface Window {
    ApplePaySession?: typeof ApplePaySession;
  }
}

export function BraintreeApplePay({ onSuccess, clientInstance, amount, error }: BraintreeApplePayProps) {
  const applePayInstance = useRef<ApplePay | null>(null);
  const { trackEvent } = useAnalytics();
  // Initialize Apple Pay with the Braintree client instance
  const [errors, setErrors] = useState<string | null>(null);

  const errorMessage = error?.message ?? errors;

  useEffect(() => {
    async function createApplePaymentInstance() {
      try {
        if (!applePayInstance.current) {
          const applePaymentInstance = await applePay.create({
            client: clientInstance,
          });
          trackEvent('BRAINTREE_APPLE_PAY_INSTANCE_CREATED');
          applePayInstance.current = applePaymentInstance;
        }
      } catch (error) {
        reportError({ error });
        setErrors('Failed to initialize Apple Pay.');
      }
    }
    void createApplePaymentInstance();
  }, [clientInstance, trackEvent]);

  // Create Apple Pay token on submit
  function createApplePayToken(): void {
    if (!applePayInstance.current) {
      reportError({ error: 'Apple Pay instance failed to load.' });
      setErrors('Failed to initialize Apple Pay.');
      return;
    }
    trackEvent('BRAINTREE_APPLE_PAY_CLICKED');
    const paymentRequest: ApplePayPaymentRequest = applePayInstance.current.createPaymentRequest({
      total: {
        label: MERCHANT_NAME,
        amount,
      },
    });

    if (window.ApplePaySession !== undefined) {
      const session = new window.ApplePaySession(APPLE_PAY_VERSION, paymentRequest);
      session.onvalidatemerchant = (event: { validationURL: string }) => {
        if (!applePayInstance.current) {
          return;
        }
        applePayInstance.current
          .performValidation({
            validationURL: event.validationURL,
            displayName: MERCHANT_NAME,
          })
          .then((merchantSession) => {
            session.completeMerchantValidation(merchantSession);
            trackEvent('BRAINTREE_APPLE_PAY_MERCHANT_VALIDATED');
          })
          .catch((error) => {
            reportError({ error });
            session.abort();
          });
      };

      session.onpaymentauthorized = (event: { payment: { token: string } }) => {
        if (!applePayInstance.current) {
          return;
        }
        applePayInstance.current
          .tokenize({
            token: event.payment.token,
          })
          .then((payload: ApplePayPayload) => {
            if (payload.nonce) {
              session.completePayment(0);
              trackEvent('BRAINTREE_APPLE_PAY_PAYMENT_AUTHORIZED');
              return onSuccess(payload.nonce);
            }
          })
          .catch((error) => {
            session.completePayment(1);
            trackEvent('BRAINTREE_APPLE_PAY_PAYMENT_FAILED');
            setErrors('Unable to process payment.');
            reportError({ error });
          });
      };

      session.oncancel = () => {
        return;
      };

      try {
        session.begin();
      } catch (error) {
        reportError({ error });
        session.abort();
      }
    }
  }

  return (
    <>
      {errorMessage && (
        <Alert
          severity={AlertSeverity.ERROR}
          titleGutterBottom={false}
          title='Apple Pay Error'
          text={typeof errorMessage === 'string' ? errorMessage : 'An error occurred.'}
        />
      )}
      <ApplePayButton
        type='button'
        onClick={() => {
          void createApplePayToken();
        }}
      />
    </>
  );
}

const ApplePayButton = styled('button')`
  height: 42px;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-style: black;
`;
